import { Component, ViewChild, ElementRef, OnChanges, Input, OnInit } from '@angular/core';
import * as d3 from 'd3';
import { HierarchyNode } from 'd3';

import { SunburstNode } from './sunburst-node';
import { UiService } from '@services/ui.service';

@Component({
    selector: 'app-sunburst',
    templateUrl: './sunburst.component.html',
    styleUrls: ['./sunburst.component.scss']
})
export class SunburstComponent implements OnChanges {
    @Input() rootNode: SunburstNode;
    @Input() chartSize = 250;

    @ViewChild('sunburst', { static: true }) set content(content: ElementRef) {
        this.chartContainer = content;
    }

    colors = [];

    colorsTree: {
        code: string,
        color: string
    }[];

    vis: any;

    isDetailsVisible: boolean;
    showedLabel: string;
    showedPercent: string;
    showedValue: number;

    private chartContainer: ElementRef;

    constructor(
        private uiSrv: UiService
    ) {
        this.colors = this.uiSrv.getColors();
    }

    ngOnChanges() {
        this.initChart();
    }

    private initChart(): void {
        this.cleanUpChart();

        this.colorsTree = [];

        if (this.rootNode) {
            this.setPartsColors(this.rootNode.children);
            this.drawSunburst();
        }
    }

    // create sunburst chart objects with D3.js
    private drawSunburst(): void {
        const json: SunburstNode = JSON.parse(JSON.stringify(this.rootNode));
        const element = this.chartContainer.nativeElement;
        const width = this.chartSize;
        const height = width;
        const radius = width / 2;

        this.vis = d3.select(element).append("svg:svg")
            .attr("width", width)
            .attr("height", height)
            .append("svg:g")
            .attr("id", `container`)
            .attr("transform", "translate(" + width / 2 + "," + height / 2 + ")");

        // Bounding circle underneath the sunburst, to make it easier to detect
        // when the mouse leaves the parent g.
        this.vis.append("svg:circle")
            .attr("r", radius)
            .style("opacity", 0);

        const partition = d3.partition()
            .size([2 * Math.PI, radius * radius]);

        const arc = d3.arc()
            .startAngle((d: any) => d.x0)
            .endAngle((d: any) => d.x1)
            .innerRadius((d: any) => Math.sqrt(d.y0))
            .outerRadius((d: any) => Math.sqrt(d.y1));

        const root = d3.hierarchy(json, d => d.children)
            .sum(d => d.children.length ? 0 : d.ratio);

        const nodes = partition(root).descendants();

        this.vis.data([json]).selectAll("path")
            .data(nodes)
            .enter().append("svg:path")
            .attr("display", d => d.depth ? null : "none")
            .attr("d", arc)
            .attr("fill-rule", "inherit")
            .style("fill", d => this.fillColor(d))
            .style('stroke', '#FFF')
            .style("opacity", 1)
            .on("mouseover", d => this.highlightPath(d))
            .on("mouseleave", () => this.mouseleave());

    }

    // Set the color of each node
    private fillColor(d: HierarchyNode<SunburstNode>) {
        const found = this.colorsTree.find(color => color.code === d.data.code);
        if (found) {
            return found.color;
        }

        return '#51626f';
    }

    private cleanUpChart() {
        const element = this.chartContainer.nativeElement;
        d3.select(element.querySelector('svg')).remove();
    }

    private mouseleave(): void {
        this.clearHighlight();
    }

    // Fade all but the current sequence.
    private highlightPath(selected: HierarchyNode<SunburstNode>): void {
        const percentage = (100 * selected.data.ratio).toFixed(2);
        const percentageString = percentage + "%";

        this.showedPercent = percentageString;
        this.showedLabel = selected.data.text ? selected.data.text.shortLabel : "Text not found";
        this.showedValue = selected.data.amount;
        this.isDetailsVisible = true;

        const sequenceArray = selected.ancestors().reverse();
        sequenceArray.shift();

        // Fade all the segments.
        this.vis.selectAll("path").style("opacity", 0.3);

        // Then highlight only those that are an ancestor of the current segment.
        this.vis.selectAll("path")
            .filter(node => {
                return (sequenceArray.indexOf(node) >= 0);
            })
            .style("opacity", 1);
    }

    // Restore everything to full opacity when moving off the visualization.
    private clearHighlight(): void {
        // Transition each segment to full opacity and then reactivate it.
        this.vis.selectAll("path").style("opacity", 1);

        this.isDetailsVisible = false;
    }

    // Create gradient colors array to apply in chart for each node
    private setPartsColors(nodes: SunburstNode[], sendColor?: string): void {
        nodes.map((node, index) => {
            const color = sendColor || this.colors[index];
            this.colorsTree.push({
                code: node.code,
                color
            });

            if (node.children && node.children.length) {
                this.setPartsColors(node.children, color);
            }
        });
    }
}
